import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma, formatDateWithoutTime, getMenuTypeName } from 'helpers/format'
import { Col, Row, Space } from 'antd'
import PdfCompanyInfo from 'Pages/Purchase/components/PdfCompanyInfo'
import { PdfPrintFormat, PdfPrintLiteFormat } from 'Components/form'
import { ThaiBaht } from 'thai-baht-text-ts'
import { ReturnData, ReturnProduct } from 'Store/return-order/types'
import { menuReturnPaymentTypes } from 'helpers/constanst'

type Props = {
  detail?: ReturnData
  currentUser?: any
  isLite?: boolean
}

const ReturnOrderPDF: React.FunctionComponent<Props> = props => {
  const { detail, currentUser, isLite } = props

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderInfoItem = (title: any, value: any, justify: 'start' | 'end' = 'start') => {
    return (
      <Row gutter={[8, 8]} justify={justify}>
        <Col>
          <Space wrap={true}>
            <InfoTitle>
              {title} : {value || '-'}
            </InfoTitle>
          </Space>
        </Col>
      </Row>
    )
  }

  const renderCustomerInfo = () => {
    return (
      <div>
        {renderInfoItem('เลขที่สั่งซื้อ', detail?.order_no)}
        {renderInfoItem('ลูกค้า', detail?.customer_fullname)}
        {renderInfoItem('รับคืนโดย', detail?.officer)}
      </div>
    )
  }

  const renderPurchaseInfo = () => {
    return <div>{renderInfoItem('วันที่', formatDateWithoutTime(detail?.created_at || ''), 'end')}</div>
  }

  const summaryFooter = (
    <Row justify="space-between">
      <Col span={12}>
        <InfoTitle style={{ textAlign: 'left' }}>
          ยอดเงินคืนสุทธิ (ตัวอักษร) {ThaiBaht(detail?.total_amount || 0)}
        </InfoTitle>
      </Col>
      <Col span={8}>
        <Row justify="space-between">
          <Col>
            <InfoTitle>ยอดเงินคืนสุทธิ</InfoTitle>
          </Col>
          <Col>
            <InfoTitle>{numberWithComma(detail?.total_amount || 0, true)} บาท</InfoTitle>
          </Col>
        </Row>
      </Col>
    </Row>
  )

  const renderProductName = (record: ReturnProduct) => {
    const isSameTitle = record.title == record.product_option_title
    return (
      <>
        <RowTile>{record.title}</RowTile>
        {!isSameTitle && <Description>{record.product_option_title}</Description>}
      </>
    )
  }

  const tableHeader = (
    <thead>
      <th>ลำดับ</th>
      <th className="left">รหัสสินค้า</th>
      <th className="left" style={{ width: '150px' }}>
        ชื่อสินค้า
      </th>
      <th className="left">ยี่ห้อ</th>
      <th className="left">จำนวน(ซื้อ)</th>
      <th className="right">ราคา/หน่วย</th>
      <th className="right">ราคารวม</th>
      <th className="right">จำนวน(คืน)</th>
      <th className="right">ยอดเงินคืน</th>
    </thead>
  )

  const tableBody = (
    <tbody>
      {detail?.products &&
        detail.products.map((item: ReturnProduct, index: number) => {
          return (
            <tr key={index}>
              <td>{renderInfo(index + 1)}</td>
              <td className="left">{renderInfo(item?.code || '-')}</td>
              <td className="left">{renderInfo(renderProductName(item))}</td>
              <td className="left">{renderInfo(item?.brand || '-')}</td>
              <td className="left">
                {renderInfo(`${numberWithComma(item?.quantity || 0)} ${item?.product_uom || ''}`)}
              </td>
              <td className="right">{renderInfo(numberWithComma(item?.price || 0, true))}</td>
              <td className="right">{renderInfo(numberWithComma(item?.total_price || 0, true))}</td>
              <td className="right">
                {renderInfo(`${numberWithComma(item?.return_qty || 0)} ${item.return_product_uom || ''}`)}
              </td>
              <td className="right">{renderInfo(numberWithComma(item?.return_amount || 0, true))}</td>
            </tr>
          )
        })}
    </tbody>
  )

  const moreFooter = (
    <Row justify="start">
      <Col span={24}>{renderInfoItem('เหตุผลการคืน', detail?.reason || '')}</Col>
      <Col span={24}>
        {renderInfoItem(
          'ช่องทางการรับเงินคืน',
          `${getMenuTypeName(menuReturnPaymentTypes, detail?.return_type || '')}`,
        )}
      </Col>
      <Col span={24}>{renderInfoItem('หมายเหตุ', detail?.remark || '')}</Col>
    </Row>
  )

  return isLite ? (
    <PdfPrintLiteFormat
      companyInfo={<PdfCompanyInfo merchant={currentUser?.activeMerchant} hasFullAddress={true} />}
      title={`ใบรับคืนสินค้า/ใบลดหนี้`}
      leftInfo={renderCustomerInfo()}
      rightInfo={renderPurchaseInfo()}
      footerSpan={11}
      summaryFooter={summaryFooter}
      signPosition={[`ผู้รับคืนสินค้า(พนักงาน)`, 'ผู้รับเงิน(ลูกค้า)']}
      thead={tableHeader}
      tbody={tableBody}
      moreFooter={moreFooter}
    />
  ) : (
    <PdfPrintFormat
      companyInfo={<PdfCompanyInfo merchant={currentUser?.activeMerchant} hasFullAddress={true} />}
      title={`ใบรับคืนสินค้า/ใบลดหนี้`}
      leftInfo={renderCustomerInfo()}
      rightInfo={renderPurchaseInfo()}
      footerSpan={11}
      summaryFooter={summaryFooter}
      signPosition={[`ผู้รับคืนสินค้า(พนักงาน)`, 'ผู้รับเงิน(ลูกค้า)']}
      thead={tableHeader}
      tbody={tableBody}
      moreFooter={moreFooter}
    />
  )
}

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`
const Description = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  white-space: pre-line;
  text-align: left;
`
export default ReturnOrderPDF
